import { useState } from "react";
import { durationOptions, regionOptions, typeOptions, yearOptions } from "@/mocks/categories";
import type { Filters } from "@/lib/videos";

interface FilterPanelProps {
  filters: Filters;
  typeLocked?: boolean;
  onTypesChange: (values: string[]) => void;
  onRegionsChange: (values: string[]) => void;
  onYearsChange: (values: string[]) => void;
  onDurationsChange: (values: string[]) => void;
  onReset: () => void;
}

interface GroupProps {
  title: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  collapsedCount?: number;
}

function FilterGroup({ title, options, selected, onChange, collapsedCount = 6 }: GroupProps) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = options.length > collapsedCount;
  const visible = expanded ? options : options.slice(0, collapsedCount);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="border-b border-background-300/40 pb-4 mb-4 last:border-0 last:mb-0 last:pb-0">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-xs font-medium text-foreground-200 tracking-wide">{title}</h3>
        {selected.length > 0 && (
          <span className="text-[10px] text-accent-400 font-label">已选 {selected.length}</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {visible.map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => toggle(option.value)}
              className={`h-8 px-2 rounded-sm text-[11px] transition-colors cursor-pointer truncate ${
                active
                  ? "bg-primary-500/20 text-primary-300 border border-primary-500/60"
                  : "bg-background-200/50 text-foreground-400 border border-transparent hover:text-foreground-100 hover:bg-background-200"
              }`}
              title={option.label}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 text-[11px] text-foreground-500 hover:text-accent-400 transition-colors flex items-center gap-0.5 cursor-pointer"
        >
          {expanded ? "收起" : `展开全部（${options.length}）`}
          <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>
      )}
    </div>
  );
}

export default function FilterPanel({
  filters,
  typeLocked = false,
  onTypesChange,
  onRegionsChange,
  onYearsChange,
  onDurationsChange,
  onReset,
}: FilterPanelProps) {
  const activeCount =
    filters.types.length + filters.regions.length + filters.years.length + filters.durations.length;

  return (
    <div className="rounded-lg bg-background-100 border border-background-200/70 p-4 lg:sticky lg:top-24">
      <div className="flex items-center justify-between pb-3 mb-4 border-b border-background-300/40">
        <div className="flex items-center gap-1.5">
          <i className="ri-filter-3-line text-accent-400" />
          <span className="text-sm font-medium text-foreground-50">筛选</span>
          {activeCount > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-background-50 text-[10px] flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onReset}
          className="text-[11px] text-foreground-400 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          重置
        </button>
      </div>

      {typeLocked ? (
        <div className="border-b border-background-300/40 pb-4 mb-4">
          <h3 className="text-xs font-medium text-foreground-200 tracking-wide mb-2.5">类型</h3>
          <p className="text-[11px] text-foreground-500">已通过顶部标签锁定类型，如需调整请切换标签。</p>
        </div>
      ) : (
        <FilterGroup title="类型" options={typeOptions} selected={filters.types} onChange={onTypesChange} />
      )}
      <FilterGroup title="地区" options={regionOptions} selected={filters.regions} onChange={onRegionsChange} />
      <FilterGroup title="年份" options={yearOptions} selected={filters.years} onChange={onYearsChange} />
      <FilterGroup title="时长" options={durationOptions} selected={filters.durations} onChange={onDurationsChange} />
    </div>
  );
}